/**
 * Catalogo tipologie di turno: fasce orarie configurabili (nome, dalle,
 * alle) usate sia dall'editor manuale di Copertura sia dal futuro motore di
 * generazione automatica. Nessuna struttura di turni è hardcoded nel
 * codice: questa è l'unica fonte, definita da chi configura il sistema.
 */
import React, { useState } from "react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import {
  Card,
  Spinner,
  ErrorBox,
  SimpleTable,
} from "../../components/ui/basics";

const FORM_INIT = {
  codice: "",
  nome: "",
  orainizio: "06:00",
  orafine: "14:00",
};

export default function TipologieTurnoSection() {
  const { apiRequest } = useAuth();
  const { data, loading, error, reload } = useApi("/planning/turnitipo");
  const [form, setForm] = useState(FORM_INIT);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await apiRequest("/planning/turnitipo", {
        method: "POST",
        body: JSON.stringify({
          codice: form.codice.trim().toUpperCase(),
          nome: form.nome.trim(),
          orainizio: form.orainizio,
          orafine: form.orafine,
        }),
      });
      setForm(FORM_INIT);
      reload();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const elimina = async (r) => {
    if (!window.confirm(`Eliminare la tipologia "${r.nome}"?`)) return;
    await apiRequest(`/planning/turnitipo/${r.id}`, { method: "DELETE" });
    reload();
  };

  return (
    <Card
      title="Tipologie turno"
      subtitle="Fasce orarie configurabili — usate nell'editor Copertura invece di orari liberi. Definiscile secondo la struttura reale dei turni delle RSA."
    >
      {data?.source === "default" && (
        <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2 mb-3">
          ⚠ Entità non ancora creata sul tenant Dataverse (gate schema, richiede
          approvazione). Il catalogo è vuoto finché non viene eseguito lo script
          di setup.
        </p>
      )}
      {loading && <Spinner />}
      {error && <ErrorBox message={error} onRetry={reload} />}

      {data && (
        <SimpleTable
          rowKey={(r) => r.id}
          columns={[
            { key: "codice", label: "Codice" },
            { key: "nome", label: "Nome" },
            {
              key: "orario",
              label: "Orario",
              render: (r) => `${r.orainizio}–${r.orafine} (${r.durataOre}h)`,
            },
            {
              key: "azioni",
              label: "",
              render: (r) => (
                <button
                  onClick={() => elimina(r)}
                  className="text-red-600 hover:underline text-xs"
                >
                  Elimina
                </button>
              ),
            },
          ]}
          rows={data.items}
        />
      )}

      <form onSubmit={submit} className="flex flex-wrap gap-3 items-end mt-4">
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1">Codice</span>
          <input
            type="text"
            value={form.codice}
            onChange={(e) => setForm({ ...form, codice: e.target.value })}
            required
            placeholder="MATTINA"
            className="border border-gray-300 rounded px-2 py-1.5 w-32"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1">Nome</span>
          <input
            type="text"
            value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
            required
            placeholder="Turno mattina"
            className="border border-gray-300 rounded px-2 py-1.5 w-48"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1">Dalle</span>
          <input
            type="time"
            value={form.orainizio}
            onChange={(e) => setForm({ ...form, orainizio: e.target.value })}
            required
            className="border border-gray-300 rounded px-2 py-1.5"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1">
            Alle (oltre mezzanotte = giorno dopo)
          </span>
          <input
            type="time"
            value={form.orafine}
            onChange={(e) => setForm({ ...form, orafine: e.target.value })}
            required
            className="border border-gray-300 rounded px-2 py-1.5"
          />
        </label>
        <button
          type="submit"
          disabled={saving}
          className="bg-primary-600 text-white text-sm px-4 py-1.5 rounded hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? "Salvataggio…" : "+ Nuova tipologia"}
        </button>
      </form>
      {formError && <p className="text-xs text-red-600 mt-2">{formError}</p>}
    </Card>
  );
}
